/**
 * Where a model's latency actually sits, against the slowest on the page.
 *
 * A dot for the typical part (p50) and a bar reaching p95 — the two numbers a
 * line is planned around. Deliberately NOT a gauge: a gauge spends a lot of
 * space on one value and, with five of them beside each other, still leaves you
 * comparing needles. Sharing one scale across every row means the comparison is
 * the length, which is the thing the eye is actually good at.
 *
 * On this installation the fastest model reads 31ms and the slowest 1967 — a
 * shared linear scale makes that difference the whole visual, which is correct:
 * it IS the story.
 */
export function RangeBar({
  p50,
  p95,
  scaleMax,
  color = 'var(--accent)',
}: {
  p50: number;
  p95: number;
  scaleMax: number;
  color?: string;
}) {
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / (scaleMax || 1)) * 100))}%`;
  /*
    A clamped bar looks like a bar that merely reaches the end, which is the
    quietest kind of lie a chart tells. When a value is off the scale the row
    says so with an arrow — the figure beside it gives the real number.
  */
  const overflows = p95 > scaleMax;
  return (
    <div
      style={{ position: 'relative', height: 12, background: 'var(--bg)', borderRadius: 6 }}
      title={`typical ${p50} ms · 95th percentile ${p95} ms`}
    >
      {/* p50 → p95, the band the line has to absorb */}
      <div
        style={{
          position: 'absolute',
          left: pct(Math.min(p50, p95)),
          width: `calc(${pct(Math.abs(p95 - p50))} + 3px)`,
          top: 3,
          height: 6,
          background: color,
          opacity: 0.35,
          borderRadius: 3,
        }}
      />
      {overflows && (
        <span
          aria-label="beyond the scale"
          title={`${p95} ms — past the end of this scale`}
          style={{
            position: 'absolute',
            right: -12,
            top: -3,
            fontSize: 12,
            color: 'var(--warn)',
            lineHeight: '18px',
          }}
        >
          ›
        </span>
      )}
      {/* p50 itself: the part that comes off the line most of the time */}
      <div
        style={{
          position: 'absolute',
          left: pct(p50),
          top: 1,
          width: 3,
          height: 10,
          background: color,
          borderRadius: 2,
          transform: 'translateX(-1px)',
        }}
      />
    </div>
  );
}
